import i18n from '../i18n/config';
import { useTranslation } from 'react-i18next';
// frontend/src/pages/OrdersPage.jsx - FIXED: PendingPaymentForm fuera del componente principal
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Typography,
    Box,
    Paper,
    Card,
    CardContent,
    Grid,
    Chip,
    Button,
    List,
    ListItem,
    ListItemText,
    Divider,
    Alert,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Tooltip,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import {
    Timeline,
    TimelineItem,
    TimelineSeparator,
    TimelineConnector,
    TimelineContent,
    TimelineOppositeContent,
    TimelineDot,
} from '@mui/lab';
import {
    Receipt as ReceiptIcon,
    Visibility as ViewIcon,
    Cancel as CancelIcon,
    Payment as PaymentIcon,
    CheckCircle as CheckIcon,
    Schedule as ScheduleIcon,
    ExpandMore as ExpandMoreIcon,
    Download as DownloadIcon,
    TrackChanges as TrackIcon
} from '@mui/icons-material';

import { ecommerceApi, cartHelpers, orderHelpers } from '../services/ecommerceApi';
import { useNotification } from '../components/common/GlobalNotifications';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

// ✅ Inicializar Stripe FUERA del componente
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// ✅ COMPONENTE SEPARADO - Fuera del componente principal
const PendingPaymentForm = ({ order, paymentIntent, onSuccess, onCancel }) => {
  const { t } = useTranslation();
    const stripe = useStripe();
    const elements = useElements();
    const { showError, showSuccess } = useNotification();
    const [processing, setProcessing] = useState(false);

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!stripe || !elements || !paymentIntent) {
            showError(t('hardcoded.pages_orderspage.stripe_no_esta_listo_por'));
            return;
        }

        setProcessing(true);

        try {
            const cardElement = elements.getElement(CardElement);

            console.log(t('hardcoded.pages_orderspage.confirmando_pago_con_stripe'));

            const { error, paymentIntent: confirmedIntent } = await stripe.confirmCardPayment(
                paymentIntent.client_secret,
                {
                    payment_method: {
                        card: cardElement,
                        billing_details: {
                            name: `${order.shipping_address?.first_name || ''} ${order.shipping_address?.last_name || ''}`.trim(),
                            email: order.user?.email || order.shipping_address?.email || '',
                        },
                    },
                }
            );

            if (error) {
                console.error(t('hardcoded.pages_orderspage.error_de_pago'), error);
                showError(t('hardcoded.pages_orderspage.error_de_pago_message', { message: error.message }));
                setProcessing(false);
                return;
            }

            console.log(t('hardcoded.pages_orderspage.pago_confirmado'), confirmedIntent);

            // ✅ Verificar el estado del pago
            if (confirmedIntent.status === 'succeeded') {
                showSuccess(t('hardcoded.pages_orderspage.pago_procesado_exitosamente'));

                // ✅ Esperar un poco para que el webhook procese
                setTimeout(() => {
                    onSuccess();
                }, 2000);
            } else if (confirmedIntent.status === 'processing') {
                showSuccess(t('hardcoded.pages_orderspage.pago_en_proceso_te_notificaremos'));
                setTimeout(() => {
                    onSuccess();
                }, 2000);
            } else if (confirmedIntent.status === 'requires_payment_method') {
                showError(t('hardcoded.pages_orderspage.el_pago_requiere_un_metodo'));
                setProcessing(false);
            } else {
                showError(t('hardcoded.pages_orderspage.estado_de_pago_inesperado_status', { status: confirmedIntent.status }));
                setProcessing(false);
            }

        } catch (err) {
            console.error(t('hardcoded.pages_orderspage.error_procesando_pago'), err);
            showError(t('hardcoded.pages_orderspage.error_al_procesar_el_pago') + err.message);
            setProcessing(false);
        }
    };

    return (
        <Box component="form" onSubmit={handleSubmit}>
            <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2" gutterBottom>
                    <strong>{i18n.t('hardcoded.pages_orderspage.total_a_pagar')}</strong> {cartHelpers.formatCurrency(order.total)}
                </Typography>
                <Typography variant="caption">
                    {i18n.t('hardcoded.pages_orderspage.orden')}{order.order_number || t('hardcoded.pages_orderspage.not_available')}
                </Typography>
            </Alert>

            <Box sx={{
                border: '1px solid #ddd',
                borderRadius: 1,
                p: 2,
                mb: 3
            }}>
                <CardElement options={{
                    style: {
                        base: {
                            fontSize: '16px',
                            color: '#424770',
                            '::placeholder': { color: '#aab7c4' }
                        }
                    }
                }} />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button onClick={onCancel} disabled={processing}>
                    {t('common.cancel')}</Button>
                <Button
                    type="submit"
                    variant="contained"
                    disabled={!stripe || processing}
                    startIcon={processing && <CircularProgress size={20} color="inherit" />}
                >
                    {processing ? t('hardcoded.pages_orderspage.procesando_pago') : t('hardcoded.pages_checkoutpage.pagar_carthelpersform', { cartHelpersform: cartHelpers.formatCurrency(order.total) })}
                </Button>
            </Box>
        </Box>
    );
};

// ✅ COMPONENTE PRINCIPAL
const OrdersPage = () => {
  const { t } = useTranslation();
    const navigate = useNavigate();
    const { showSuccess, showError } = useNotification();

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [orderDetailOpen, setOrderDetailOpen] = useState(false);
    const [trackingOpen, setTrackingOpen] = useState(false);
    const [trackingData, setTrackingData] = useState(null);
    const [cancellingOrder, setCancellingOrder] = useState(false);

    const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
    const [selectedOrderForPayment, setSelectedOrderForPayment] = useState(null);
    const [paymentIntentForPending, setPaymentIntentForPending] = useState(null);

    useEffect(() => {
        loadOrders();
    }, []);

    const loadOrders = async () => {
        setLoading(true);
        try {
            const response = await ecommerceApi.orders.getAll();
            setOrders(response.data);
        } catch (error) {
            console.error('Error loading orders:', error);
            showError(t('hardcoded.pages_orderspage.error_al_cargar_las_ordenes'));
        } finally {
            setLoading(false);
        }
    };

    const handleViewOrder = (order) => {
        setSelectedOrder(order);
        setOrderDetailOpen(true);
    };

    const handleTrackOrder = async (order) => {
        try {
            const response = await ecommerceApi.orders.track(order.id);
            setTrackingData(response.data);
            setTrackingOpen(true);
        } catch (error) {
            showError(t('hardcoded.pages_orderspage.error_al_obtener_informacion_de'));
        }
    };

    const handleCancelOrder = async (order) => {
        if (!window.confirm(t('hardcoded.pages_orderspage.estas_seguro_de_que_quieres'))) {
            return;
        }

        setCancellingOrder(true);
        try {
            await ecommerceApi.orders.cancel(order.id);
            showSuccess(t('hardcoded.pages_orderspage.orden_cancelada_exitosamente'));
            loadOrders();
        } catch (error) {
            showError(error.response?.data?.detail || t('hardcoded.pages_orderspage.error_al_cancelar_la_orden'));
        } finally {
            setCancellingOrder(false);
        }
    };

    const handleDownloadInvoice = async (order) => {
        try {
            const response = await ecommerceApi.orders.getInvoice(order.id);

            const invoiceWindow = window.open('', t('hardcoded.components_operators_operatorcontactmenu._blank'));
            invoiceWindow.document.write(t('hardcoded.pages_orderspage.html_headtitlefactura_order_numbertitlehead_body_h2factura', { order_number: order.order_number, order_number: order.order_number, newDateordercre: new Date(order.created_at).toLocaleDateString(), cartHelpersform: cartHelpers.formatCurrency(order.total) }));
            invoiceWindow.document.close();
            invoiceWindow.print();
        } catch (error) {
            showError(t('hardcoded.pages_orderspage.error_al_descargar_la_factura'));
        }
    };

    const handlePayPendingOrder = async (order) => {
        try {
            const paymentResponse = await ecommerceApi.orders.createPaymentIntent(order.id);

            setSelectedOrderForPayment(order);
            setPaymentIntentForPending(paymentResponse.data);
            setPaymentDialogOpen(true);
        } catch (error) {
            console.error(t('hardcoded.pages_orderspage.payment_intent_error'), error);
            showError(t('hardcoded.pages_orderspage.error_al_procesar_pago') + (error.response?.data?.detail || error.message));
        }
    };

    const handlePaymentSuccess = async () => {
        if (!selectedOrderForPayment) return;

        try {
            // ✅ Verificar el estado del pago en el backend
            console.log(t('hardcoded.pages_orderspage.verificando_estado_del_pago'));
            const statusResponse = await ecommerceApi.orders.getPaymentStatus(selectedOrderForPayment.id);

            console.log(t('hardcoded.pages_orderspage.estado_del_pago'), statusResponse.data);

            if (statusResponse.data.payment_status === 'paid') {
                showSuccess(t('hardcoded.pages_orderspage.pago_confirmado_tu_orden_ha'));
            } else if (statusResponse.data.stripe_status === 'succeeded') {
                showSuccess(t('hardcoded.pages_orderspage.pago_confirmado_en_stripe_actualizando'));
            } else {
                showSuccess(t('hardcoded.pages_orderspage.pago_procesado_verificando'));
            }

        } catch (error) {
            console.error(t('hardcoded.pages_orderspage.error_verificando_estado'), error);
            showSuccess(t('hardcoded.pages_orderspage.pago_procesado_refrescando_ordenes'));
        }

        // Cerrar diálogo y recargar
        setPaymentDialogOpen(false);
        setSelectedOrderForPayment(null);
        setPaymentIntentForPending(null);

        // Recargar después de un delay
        setTimeout(() => {
            loadOrders();
        }, 1000);
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={60} />
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" fontWeight={600} gutterBottom>
                    {t('hardcoded.pages_orderspage.mis_pedidos')}</Typography>
                <Typography variant="body1" color="text.secondary">
                    {t('hardcoded.pages_orderspage.revisa_el_estado_de_tus')}</Typography>
            </Box>

            {/* Orders List */}
            {orders.length === 0 ? (
                <Paper sx={{ p: 6, textAlign: 'center' }}>
                    <ReceiptIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                        {t('hardcoded.pages_orderspage.no_tienes_pedidos_aun')}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {i18n.t('hardcoded.pages_orderspage.explora_nuestra_tienda_y_haz')}</Typography>
                    <Button variant="contained" onClick={() => navigate('/store')}>
                        {t('hardcoded.pages_cartpage.ir_a_la_tienda')}</Button>
                </Paper>
            ) : (
                <Grid container spacing={3}>
                    {orders.map((order) => (
                        <Grid item xs={12} key={order.id}>
                            <Card>
                                <CardContent>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Typography variant="h6" fontWeight={600}>
                                                #{order.order_number || t('hardcoded.pages_orderspage.not_available')}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {new Date(order.created_at).toLocaleDateString('es-MX', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric'
                                                })}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {order.item_count || 0} {i18n.t('cart.items')}</Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={2}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                <Chip
                                                    label={order.status_display || order.status}
                                                    color={orderHelpers.getStatusColor(order.status)}
                                                    size="small"
                                                />
                                                <Chip
                                                    label={order.payment_status || 'pending'}
                                                    color={order.payment_status === 'paid' ? 'success' : 'warning'}
                                                    size="small"
                                                    icon={<PaymentIcon />}
                                                />
                                            </Box>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={2}>
                                            <Typography variant="h6" color="primary" fontWeight={600}>
                                                {cartHelpers.formatCurrency(order.total)}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={2}>
                                            {order.tracking_number ? (
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                        {i18n.t('hardcoded.pages_orderspage.rastreo')}</Typography>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {order.carrier || t('hardcoded.components_admin_ordermanagement.transportista')}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {order.tracking_number}
                                                    </Typography>
                                                </Box>
                                            ) : order.shipping_address ? (
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                        {t('hardcoded.pages_orderspage.envio_a')}</Typography>
                                                    <Typography variant="body2" fontWeight={600} noWrap>
                                                        {(order.shipping_address.first_name || '') + ' ' + (order.shipping_address.last_name || '')}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" noWrap>
                                                        {[order.shipping_address.city, order.shipping_address.state].filter(Boolean).join(', ')}
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    {t('hardcoded.pages_orderspage.sin_informacion_de_envio')}</Typography>
                                            )}
                                        </Grid>

                                        <Grid item xs={12} md={3}>
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                <Tooltip title={t('hardcoded.components_admin_ordermanagement.ver_detalles')}>
                                                    <IconButton size="small" onClick={() => handleViewOrder(order)}>
                                                        <ViewIcon />
                                                    </IconButton>
                                                </Tooltip>

                                                {order.payment_status === 'pending' && order.status !== 'cancelled' && (
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        size="small"
                                                        startIcon={<PaymentIcon />}
                                                        onClick={() => handlePayPendingOrder(order)}
                                                    >
                                                        {i18n.t('hardcoded.pages_orderspage.pagar_ahora')}</Button>
                                                )}

                                                {order.tracking_number && (
                                                    <Tooltip title={t('hardcoded.pages_orderspage.rastrear_envio')}>
                                                        <IconButton size="small" onClick={() => handleTrackOrder(order)}>
                                                            <TrackIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                {order.payment_status === 'paid' && (
                                                    <Tooltip title={t('hardcoded.pages_orderspage.descargar_factura')}>
                                                        <IconButton size="small" onClick={() => handleDownloadInvoice(order)}>
                                                            <DownloadIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                {order.can_cancel && (
                                                    <Tooltip title={t('hardcoded.pages_orderspage.cancelar_orden')}>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => handleCancelOrder(order)}
                                                            disabled={cancellingOrder}
                                                        >
                                                            <CancelIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    <Accordion sx={{ mt: 2 }}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography variant="body2">
                                                {i18n.t('hardcoded.pages_orderspage.ver_productos')}{order.item_count || 0})
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <List dense>
                                                {order.items?.slice(0, 3).map((item) => (
                                                    <ListItem key={item.id}>
                                                        <ListItemText
primary={item.name || t('hardcoded.pages_orderspage.product')}
                                                            secondary={`${item.quantity || 0} x ${cartHelpers.formatCurrency(item.unit_price)}`}
                                                        />
                                                        <Typography variant="body2" fontWeight={600}>
                                                            {cartHelpers.formatCurrency(item.total_price)}
                                                        </Typography>
                                                    </ListItem>
                                                ))}
                                                {order.items?.length > 3 && (
                                                    <ListItem>
                                                        <ListItemText
                                                            primary={t('hardcoded.pages_orderspage.y_mas_productos', { count: order.items.length - 3 })}
                                                            sx={{ fontStyle: 'italic' }}
                                                        />
                                                    </ListItem>
                                                )}
                                            </List>
                                        </AccordionDetails>
                                    </Accordion>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Payment Dialog */}
            <Dialog
                open={paymentDialogOpen}
                onClose={() => setPaymentDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {i18n.t('hardcoded.pages_orderspage.completar_pago_-_orden')}{selectedOrderForPayment?.order_number || t('hardcoded.pages_orderspage.not_available')}
                </DialogTitle>
                <DialogContent>
                    {selectedOrderForPayment && paymentIntentForPending && (
                        <Elements stripe={stripePromise}>
                            <PendingPaymentForm
                                order={selectedOrderForPayment}
                                paymentIntent={paymentIntentForPending}
                                onSuccess={handlePaymentSuccess}
                                onCancel={() => setPaymentDialogOpen(false)}
                            />
                        </Elements>
                    )}
                </DialogContent>
            </Dialog>

            {/* Order Detail Dialog */}
            <Dialog
                open={orderDetailOpen}
                onClose={() => setOrderDetailOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {t('hardcoded.pages_orderspage.detalles_de_pedido')}{selectedOrder?.order_number || t('hardcoded.pages_orderspage.not_available')}
                </DialogTitle>
                <DialogContent dividers>
                    {selectedOrder && (
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" gutterBottom>
                                    {t('hardcoded.pages_cartpage.resumen_del_pedido')}</Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography>{i18n.t('hardcoded.components_admin_ordermanagement.estado')}</Typography>
                                        <Chip
                                            label={selectedOrder.status_display || selectedOrder.status}
                                            color={orderHelpers.getStatusColor(selectedOrder.status)}
                                            size="small"
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography>{i18n.t('hardcoded.components_admin_ordermanagement.pago')}</Typography>
                                        <Chip
                                            label={selectedOrder.payment_status || 'pending'}
                                            color={selectedOrder.payment_status === 'paid' ? 'success' : 'warning'}
                                            size="small"
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography>{t('hardcoded.pages_orderspage.fecha')}</Typography>
                                        <Typography>
                                            {new Date(selectedOrder.created_at).toLocaleDateString('es-MX')}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography>{i18n.t('hardcoded.components_admin_ordermanagement.total')}</Typography>
                                        <Typography variant="h6" color="primary">
                                            {cartHelpers.formatCurrency(selectedOrder.total)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" gutterBottom>
                                    {t('hardcoded.components_admin_ordermanagement.direccion_de_envio')}</Typography>
                                {selectedOrder.shipping_address ? (
                                    <>
                                        <Typography>
                                            {selectedOrder.shipping_address.first_name || ''} {selectedOrder.shipping_address.last_name || ''}
                                        </Typography>
                                        <Typography>{selectedOrder.shipping_address.address_line_1 || ''}</Typography>
                                        {selectedOrder.shipping_address.address_line_2 && (
                                            <Typography>{selectedOrder.shipping_address.address_line_2}</Typography>
                                        )}
                                        <Typography>
                                            {[selectedOrder.shipping_address.city, selectedOrder.shipping_address.state, selectedOrder.shipping_address.postal_code]
                                                .filter(Boolean)
                                                .join(', ')}
                                        </Typography>
                                        <Typography>{selectedOrder.shipping_address.country || ''}</Typography>
                                        {selectedOrder.shipping_address.phone && (
                                            <Typography variant="caption" color="text.secondary">
                                                {i18n.t('hardcoded.components_admin_ordermanagement.tel')}{selectedOrder.shipping_address.phone}
                                            </Typography>
                                        )}
                                    </>
                                ) : (
                                    <Typography color="text.secondary">{t('hardcoded.pages_orderspage.sin_direccion_de_envio')}</Typography>
                                )}
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" gutterBottom>
                                    {t('hardcoded.pages_orderspage.direccion_de_facturacion')}</Typography>
                                {selectedOrder.billing_address ? (
                                    <>
                                        {selectedOrder.billing_address.business_name && (
                                            <Typography fontWeight={600}>
                                                {selectedOrder.billing_address.business_name}
                                            </Typography>
                                        )}
                                        <Typography>
                                            {selectedOrder.billing_address.first_name || ''} {selectedOrder.billing_address.last_name || ''}
                                        </Typography>
{selectedOrder.billing_address.rfc && (
                                          <Typography variant="body2" color="text.secondary">
                                                {t('hardcoded.pages_orderspage.rfc')}: {selectedOrder.billing_address.rfc}
                                            </Typography>
                                        )}
                                        <Typography>{selectedOrder.billing_address.address_line_1 || ''}</Typography>
                                        {selectedOrder.billing_address.address_line_2 && (
                                            <Typography>{selectedOrder.billing_address.address_line_2}</Typography>
                                        )}
                                        <Typography>
                                            {[selectedOrder.billing_address.city, selectedOrder.billing_address.state, selectedOrder.billing_address.postal_code]
                                                .filter(Boolean)
                                                .join(', ')}
                                        </Typography>
                                        <Typography>{selectedOrder.billing_address.country || ''}</Typography>
                                        {(selectedOrder.billing_address.cfdi_use || selectedOrder.billing_address.cfdi_payment_method || selectedOrder.billing_address.cfdi_payment_form) && (
                                            <Box sx={{ mt: 1, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                    {t('hardcoded.pages_orderspage.datos_cfdi')}
                                                </Typography>
                                                {selectedOrder.billing_address.cfdi_use && (
                                                    <Typography variant="caption" sx={{ display: 'block' }}>
                                                        {t('hardcoded.pages_orderspage.cfdi_uso')}: {selectedOrder.billing_address.cfdi_use}
                                                    </Typography>
                                                )}
                                                {selectedOrder.billing_address.cfdi_payment_method && (
                                                    <Typography variant="caption" sx={{ display: 'block' }}>
                                                        {t('hardcoded.pages_orderspage.metodo')}{selectedOrder.billing_address.cfdi_payment_method}
                                                    </Typography>
                                                )}
                                                {selectedOrder.billing_address.cfdi_payment_form && (
                                                    <Typography variant="caption" sx={{ display: 'block' }}>
                                                        {t('hardcoded.pages_orderspage.cfdi_forma')}: {selectedOrder.billing_address.cfdi_payment_form}
                                                    </Typography>
                                                )}
                                            </Box>
                                        )}
                                    </>
                                ) : (
                                    <Typography color="text.secondary">{t('hardcoded.pages_orderspage.sin_direccion_de_facturacion')}</Typography>
                                )}
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom>
                                    {i18n.t('cart.items')}</Typography>
                                <List>
                                    {selectedOrder.items?.map((item, index) => (
                                        <React.Fragment key={item.id}>
                                            <ListItem>
                                                <ListItemText
                                                    primary={item.name || t('hardcoded.pages_orderspage.product')}
                                                    secondary={`${t('hardcoded.pages_orderspage.sku_prefix')}: ${item.sku || t('hardcoded.pages_orderspage.not_available')}`}
                                                />
                                                <Box sx={{ textAlign: 'right' }}>
                                                    <Typography variant="body2">
                                                        {item.quantity || 0} x {cartHelpers.formatCurrency(item.unit_price)}
                                                    </Typography>
                                                    <Typography variant="subtitle1" fontWeight={600}>
                                                        {cartHelpers.formatCurrency(item.total_price)}
                                                    </Typography>
                                                </Box>
                                            </ListItem>
                                            {index < selectedOrder.items.length - 1 && <Divider />}
                                        </React.Fragment>
                                    ))}
                                </List>

                                <Divider sx={{ my: 2 }} />

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography>{i18n.t('hardcoded.components_admin_kitform.subtotal')}</Typography>
                                        <Typography>{cartHelpers.formatCurrency(selectedOrder.subtotal)}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography>{i18n.t('hardcoded.components_admin_ordermanagement.iva')}</Typography>
                                        <Typography>{cartHelpers.formatCurrency(selectedOrder.tax_amount)}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography>{t('hardcoded.components_admin_ordermanagement.envio')}</Typography>
                                        <Typography>{cartHelpers.formatCurrency(selectedOrder.shipping_amount)}</Typography>
                                    </Box>
                                    {selectedOrder.discount_amount > 0 && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography color="success.main">{i18n.t('hardcoded.components_admin_ordermanagement.descuento')}</Typography>
                                            <Typography color="success.main">
                                                -{cartHelpers.formatCurrency(selectedOrder.discount_amount)}
                                            </Typography>
                                        </Box>
                                    )}
                                    <Divider />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="h6">{i18n.t('hardcoded.components_admin_ordermanagement.total')}</Typography>
                                        <Typography variant="h6" color="primary">
                                            {cartHelpers.formatCurrency(selectedOrder.total)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            {selectedOrder.customer_notes && (
                                <Grid item xs={12}>
                                    <Typography variant="h6" gutterBottom>
                                        {t('hardcoded.pages_checkoutpage.notas_del_pedido')}</Typography>
                                    <Typography variant="body2">
                                        {selectedOrder.customer_notes}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOrderDetailOpen(false)}>
                        {t('common.close')}</Button>
                </DialogActions>
            </Dialog>

            {/* Tracking Dialog */}
            <Dialog
                open={trackingOpen}
                onClose={() => setTrackingOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {t('hardcoded.pages_orderspage.rastreo_de_envio_-')}{trackingData?.order_number || t('hardcoded.pages_orderspage.not_available')}
                </DialogTitle>
                <DialogContent dividers>
                    {trackingData && (
                        <Box>
                            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Typography variant="h6" gutterBottom>
                                    {orderHelpers.getStatusDisplay(trackingData.shipping_status)}
                                </Typography>
                                {trackingData.tracking_number && (
                                    <Typography variant="body2" gutterBottom>
                                        <strong>{t('hardcoded.pages_orderspage.numero_de_rastreo')}</strong> {trackingData.tracking_number}
                                    </Typography>
                                )}
                                {trackingData.carrier && (
                                    <Typography variant="body2">
                                        <strong>{i18n.t('hardcoded.pages_orderspage.transportista')}</strong> {trackingData.carrier}
                                    </Typography>
                                )}
                            </Box>

                            <Typography variant="h6" gutterBottom>
                                {t('hardcoded.pages_orderspage.historial_de_envio')}</Typography>

                            {trackingData.updates && trackingData.updates.length > 0 ? (
                                <Timeline>
                                    {orderHelpers.formatTrackingUpdates(trackingData.updates).map((update, index) => (
                                        <TimelineItem key={update.id}>
                                            <TimelineOppositeContent color="text.secondary" sx={{ flex: 0.3 }}>
                                                <Typography variant="caption">
                                                    {update.formattedDate || 'N/A'}
                                                </Typography>
                                            </TimelineOppositeContent>
                                            <TimelineSeparator>
                                                <TimelineDot color={index === 0 ? 'primary' : 'grey'}>
                                                    {index === 0 ? <CheckIcon /> : <ScheduleIcon />}
                                                </TimelineDot>
                                                {index < trackingData.updates.length - 1 && <TimelineConnector />}
                                            </TimelineSeparator>
                                            <TimelineContent>
                                                <Typography variant="subtitle2" fontWeight={600}>
                                                    {update.description || 'N/A'}
                                                </Typography>
                                                {update.location && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        {update.location}
                                                    </Typography>
                                                )}
                                            </TimelineContent>
                                        </TimelineItem>
                                    ))}
                                </Timeline>
                            ) : (
                                <Alert severity="info">
                                    {t('hardcoded.pages_orderspage.no_hay_actualizaciones_de_envio')}</Alert>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTrackingOpen(false)}>
                        {t('common.close')}</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default OrdersPage;